const API_BASE = '/api'
const SESSION_KEY = 'ttl_manage_session_v1'

export function readSession() {
  try {
    const value = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null')
    if (!value?.token) return null
    if (value.expiresAt && Date.parse(value.expiresAt) <= Date.now()) {
      sessionStorage.removeItem(SESSION_KEY)
      return null
    }
    return value
  } catch {
    return null
  }
}

export function writeSession(session) {
  if (!session?.token) {
    sessionStorage.removeItem(SESSION_KEY)
    return
  }
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

function requestSuffix(payload) {
  return payload?.requestId ? `（錯誤編號：${payload.requestId}）` : ''
}

function createApiError(payload, response, fallbackMessage) {
  const message = payload?.message || fallbackMessage || `API 請求失敗 (${response.status})`
  const error = new Error(`${message}${requestSuffix(payload)}`)
  error.status = response.status
  error.payload = payload
  error.code = payload?.code || ''
  error.requestId = payload?.requestId || response.headers.get('X-Request-Id') || ''
  return error
}

export async function apiRequest(path, options = {}) {
  const session = readSession()
  const method = String(options.method || 'GET').toUpperCase()
  const timeoutMs = ['GET', 'HEAD'].includes(method) ? 25000 : 55000
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  const headers = new Headers(options.headers || {})
  headers.set('Accept', 'application/json')
  if (options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  if (session?.token) headers.set('Authorization', `Bearer ${session.token}`)

  let response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      ...options,
      signal: controller.signal,
      headers,
      body: options.body && !(options.body instanceof FormData)
        ? JSON.stringify(options.body)
        : options.body,
    })
  } catch (error) {
    const timedOut = error?.name === 'AbortError'
    const networkError = new Error(timedOut
      ? `管理 API 回應超過 ${Math.round(timeoutMs / 1000)} 秒，請稍後重整。`
      : `管理 API 連線失敗：${error.message}`)
    networkError.code = timedOut ? 'REQUEST_TIMEOUT' : 'NETWORK_ERROR'
    throw networkError
  } finally {
    clearTimeout(timeout)
  }

  const text = await response.text()
  let payload = {}
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      throw createApiError(
        { code: 'INVALID_API_RESPONSE', message: '管理 API 回應格式異常，請稍後重整再試。' },
        response,
      )
    }
  }

  if (!response.ok || payload?.ok === false) {
    throw createApiError(payload, response)
  }
  if (!payload || typeof payload !== 'object') {
    throw createApiError(
      { code: 'EMPTY_API_RESPONSE', message: '管理 API 未回傳有效資料。' },
      response,
    )
  }
  return payload
}

export function getStatus() {
  return apiRequest('/status')
}

export function unlockEditor(password) {
  return apiRequest('/auth/unlock', { method: 'POST', body: { password } })
}

export async function lockEditor() {
  try {
    await apiRequest('/auth/lock', { method: 'POST' })
  } finally {
    writeSession(null)
  }
}

export function getPromotions() {
  return apiRequest('/promotions')
}

export function getPromotion(id) {
  return apiRequest(`/promotions/${encodeURIComponent(id)}`)
}

export function savePromotion(id, data) {
  return apiRequest(`/promotions/${encodeURIComponent(id)}`, { method: 'PUT', body: data })
}

export function publishCampaignDm(id) {
  return apiRequest(`/promotions/${encodeURIComponent(id)}/publish-dm`, { method: 'POST', body: {} })
}

export function getCampaignCdnStatus(id, sha256) {
  return apiRequest(`/campaign-cdn/${encodeURIComponent(id)}?sha=${encodeURIComponent(sha256)}`)
}

export function getMediaProducts() {
  return apiRequest('/media')
}

export function saveMediaProduct(code, data) {
  return apiRequest(`/media/${encodeURIComponent(code)}`, { method: 'PUT', body: data })
}
